import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { SessionExpiredError } from "./api";

/**
 * Wraps an api.js call with the panel's standard session-expiry redirect and
 * toast notifications.
 *
 * const call = useApiCall();
 * const { ok, data } = await call(() => createBank(payload), {
 *   success: "Question bank created",
 * });
 *
 * Always resolves - never throws - so callers can branch on `ok` instead of
 * trying to tell a failure apart from an endpoint that legitimately returns
 * an empty payload.
 */
export const useApiCall = () => {
  const navigate = useNavigate();

  return useCallback(
    async (fn, { success, error, silent = false } = {}) => {
      try {
        const data = await fn();
        if (success) toast.success(success);
        return { ok: true, data };
      } catch (err) {
        if (err instanceof SessionExpiredError) {
          navigate("/login", { replace: true });
          return { ok: false, expired: true, error: err };
        }
        const message = err?.message || "An error occurred";
        if (!silent) toast.error(error || message);
        return { ok: false, error: err, message };
      }
    },
    [navigate]
  );
};
